import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import ReviewItemList from './ReviewItemList'
import ReviewDetailPanel from './ReviewDetailPanel'

const REFRESH_INTERVAL_MS = 60 * 1000
const HIDDEN_CATEGORY_KEYS = ['needs_review', 'geometry_reconciliation', 'aspire_tickets', 'field_issues', 'stale', 'unread_messages']

const getEventSiteName = (event = {}) => {
  if (event.areaId?.name) return event.areaId.name
  if (event.sprayGeometryId?.proposalGroupLabel) return event.sprayGeometryId.proposalGroupLabel
  if (event.sprayGeometryId?.fieldRootKey) return event.sprayGeometryId.fieldRootKey
  return 'Geometry reconciliation'
}

const getEventSummary = (event = {}) => {
  const flags = event.reviewFlags?.length ? event.reviewFlags.join(', ') : ''
  if (event.action === 'coverage_review_created') return flags || 'Coverage geometry needs review'
  if (event.action === 'area_needs_redraw') return 'Canonical Area needs redraw'
  if (event.action === 'geometry_rejected') return flags || 'Field geometry rejected'
  if (event.action === 'geometry_promoted') return 'Field geometry promoted to Area'
  return String(event.action || 'reconciliation').replace(/_/g, ' ')
}

const reconciliationEventToReviewItem = (event = {}) => ({
  _id: event._id,
  category: 'geometry_reconciliation',
  categoryLabel: 'GEOMETRY',
  scheduleItemId: event.scheduleItemId || `event:${event._id}`,
  siteName: getEventSiteName(event),
  address: event.note || '',
  summary: getEventSummary(event),
  timestamp: event.createdAt,
  data: {
    event
  }
})

const ReviewTab = ({ url, onViewInQueue }) => {
  const [items, setItems] = useState([])
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [hiddenCategories, setHiddenCategories] = useState(new Set())
  const intervalRef = useRef(null)
  const selectedItemRef = useRef(null)

  useEffect(() => { selectedItemRef.current = selectedItem }, [selectedItem])

  const fetchReviewItems = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      const [reviewResult, reconciliationResult] = await Promise.allSettled([
        axios.get(`${url}/api/herbicide-schedule/review-items`),
        axios.get(`${url}/api/herbicide-schedule/reconciliation-events`, { params: { status: 'open', limit: 100 } })
      ])

      if (reviewResult.status === 'rejected') throw reviewResult.reason

      const data = reviewResult.value.data
      let reconciliationItems = []
      if (reconciliationResult.status === 'fulfilled') {
        reconciliationItems = (reconciliationResult.value.data.events || []).map(reconciliationEventToReviewItem)
      } else if (reconciliationResult.reason?.response?.status !== 404) {
        console.warn('Error fetching reconciliation events:', reconciliationResult.reason)
      }

      const mergedItems = [...reconciliationItems, ...(data.items || [])]
      setItems(mergedItems)
      setCounts({
        ...(data.counts || {}),
        geometry_reconciliation: reconciliationItems.length,
        total: (data.counts?.total || 0) + reconciliationItems.length
      })
      // Auto-select first item if none selected (use ref to avoid stale closure)
      if (!selectedItemRef.current && mergedItems.length > 0) {
        setSelectedItem(mergedItems[0])
      }
    } catch (err) {
      console.error('Error fetching review items:', err)
      if (!silent) setError('Failed to load review items')
    }
    setLoading(false)
  }, [url])

  useEffect(() => {
    fetchReviewItems()
    intervalRef.current = setInterval(() => fetchReviewItems(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(intervalRef.current)
  }, [fetchReviewItems])

  const handleToggleCategory = useCallback((category) => {
    setHiddenCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setHiddenCategories(prev => {
      if (prev.size >= HIDDEN_CATEGORY_KEYS.length) return new Set() // all hidden → show all
      return new Set(HIDDEN_CATEGORY_KEYS)
    })
  }, [])

  const handleItemCompleted = useCallback((completedItem) => {
    // Optimistic removal
    setItems(prev => prev.filter(i => !(
      (i._id && completedItem._id && i._id === completedItem._id) ||
      (i.scheduleItemId === completedItem.scheduleItemId && i.category === completedItem.category)
    )))
    setCounts(prev => ({
      ...prev,
      [completedItem.category]: Math.max(0, (prev[completedItem.category] || 0) - 1),
      total: Math.max(0, (prev.total || 0) - 1)
    }))
    setSelectedItem(null)
  }, [])

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <ReviewItemList
        items={items}
        counts={counts}
        loading={loading}
        error={error}
        selectedItem={selectedItem}
        hiddenCategories={hiddenCategories}
        onSelect={setSelectedItem}
        onToggleCategory={handleToggleCategory}
        onToggleAll={handleToggleAll}
        onRetry={() => fetchReviewItems()}
      />
      <ReviewDetailPanel
        item={selectedItem}
        url={url}
        onViewInQueue={onViewInQueue}
        onItemCompleted={handleItemCompleted}
      />
    </div>
  )
}

export default ReviewTab
